import * as React from "react";

const SKIPPED_INPUT_TYPES = [
  "button",
  "submit",
  "checkbox",
  "radio",
  "hidden",
  "file",
];
const FOCUS_SETTLE_MS = 280;
const MIN_KEYBOARD_OVERLAP_PX = 80;
// 화면 높이 대비 키보드 추정 비율
const FALLBACK_KEYBOARD_RATIO = 0.34;
const KEYBOARD_GAP_PX = 12;
const HEADER_GAP_PX = 8;
const MIN_SCROLL_PX = 8;

/** 포커스한 입력칸이 키보드·하단 버튼에 가려지면 main 스크롤을 그 위치로 한 번 옮긴다 */
export function useKeyboardSafeScroll(
  rootRef: React.RefObject<HTMLElement | null>,
  footerRef: React.RefObject<HTMLElement | null>,
) {
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let timer = 0;
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !root.contains(target)) return;
      if (!target.matches("input, textarea, select")) return;
      if (
        target instanceof HTMLInputElement &&
        SKIPPED_INPUT_TYPES.includes(target.type)
      ) {
        return;
      }

      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (document.activeElement !== target) return;
        const scrollParent = target.closest("main");
        if (!scrollParent) return;

        const vv = window.visualViewport;
        const innerH = window.innerHeight;
        const vvBottom = vv ? vv.offsetTop + vv.height : innerH;
        const overlap = Math.max(0, innerH - vvBottom);
        const coarse = window.matchMedia("(pointer: coarse)").matches;
        // 키보드 높이를 알 수 없는 기기는 화면 비율로 가정한다
        const keyboard =
          overlap >= MIN_KEYBOARD_OVERLAP_PX
            ? overlap
            : coarse
              ? Math.round(innerH * FALLBACK_KEYBOARD_RATIO)
              : 0;

        const headerBottom =
          root.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
        let limitBottom = vvBottom - keyboard - KEYBOARD_GAP_PX;
        const footerTop = footerRef.current?.getBoundingClientRect().top;
        if (
          footerTop != null &&
          footerTop > headerBottom &&
          footerTop < limitBottom
        ) {
          limitBottom = footerTop - KEYBOARD_GAP_PX;
        }

        const rect = target.getBoundingClientRect();
        const limitTop = headerBottom + HEADER_GAP_PX;
        let delta = 0;
        if (rect.bottom > limitBottom) delta = rect.bottom - limitBottom;
        else if (rect.top < limitTop) delta = rect.top - limitTop;
        if (Math.abs(delta) < MIN_SCROLL_PX) return;

        scrollParent.scrollBy({ top: delta, behavior: "smooth" });
      }, FOCUS_SETTLE_MS);
    };

    root.addEventListener("focusin", onFocusIn);
    return () => {
      window.clearTimeout(timer);
      root.removeEventListener("focusin", onFocusIn);
    };
  }, [rootRef, footerRef]);
}
